import { apiService }from './api.service';
import { API_ENDPOINTS, STORAGE_KEYS }from '@shared/constants';
import { LoginCredentials, PermissionRule, User }from '@shared/types';

export interface LoginResult {
  token: string;
  user: User;
}

interface BackendLoginUser {
  id: number;
  email: string;
  fullName: string;
  role: string;
  position?: string;
}

interface BackendLoginResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
    user: BackendLoginUser;
  };
}

interface BackendUserProfile {
  id: number;
  email: string;
  fullName: string;
  role: string;
  position?: string;
  roleId?: number;
  departmentId?: number;
  managerId?: number;
}

class AuthService {
  private mapBackendUserToUser(user: BackendUserProfile): User {
    return {
      id: String(user.id),
      email: user.email,
      name: user.fullName,
      role: user.role,
      roleId: user.roleId,
      departmentId: user.departmentId,
      managerId: user.managerId,
      position: user.position,
    };
  }

  private async loadPermissions(): Promise<PermissionRule[]> {
    return apiService.get<PermissionRule[]>(API_ENDPOINTS.AUTH.PERMISSIONS, true);
  }

  private async enrichUser(user: User): Promise<User> {
    return { ...user, permissions: await this.loadPermissions() };
  }

  async login(credentials: LoginCredentials): Promise<LoginResult> {
    const response = await apiService.post<BackendLoginResponse>(
      API_ENDPOINTS.AUTH.LOGIN,
      credentials,
      false,
    );

    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.data.token);
    try {
      const mappedUser = await this.enrichUser(
        this.mapBackendUserToUser(response.data.user),
      );

      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(mappedUser));

      return {
        token: response.data.token,
        user: mappedUser,
      };
    } catch (error) {
      await this.clearStorage();
      throw error;
    }
  }

  async bootstrapFromToken(): Promise<User | null> {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (!token) {
      return null;
    }

    try {
      const profile = await apiService.get<BackendUserProfile>(
        API_ENDPOINTS.USERS.PROFILE,
        true,
      );

      const mappedUser = await this.enrichUser(this.mapBackendUserToUser(profile));
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(mappedUser));

      return mappedUser;
    }catch {
      await this.clearStorage();
      return null;
    }
  }

  async logout(): Promise<void> {
    try {
      await apiService.post(API_ENDPOINTS.AUTH.LOGOUT);
    }catch {
      // Bỏ qua lỗi khi logout
    }finally {
      await this.clearStorage();
    }
  }

  async clearStorage(): Promise<void> {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  }

  async getStoredUser(): Promise<User | null> {
    try {
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      return userData ? JSON.parse(userData) : null;
    }catch {
      return null;
    }
  }

  async refreshStoredPermissions(): Promise<User | null> {
    const user = await this.getStoredUser();
    if (!user) return null;
    try {
      const refreshedUser = { ...user, permissions: await this.loadPermissions() };
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(refreshedUser));
      return refreshedUser;
    } catch {
      return user;
    }
  }

  async isAuthenticated(): Promise<boolean> {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    return !!token;
  }

  async refreshToken(): Promise<string | null> {
    try {
      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      if (!refreshToken) return null;

      const response = await apiService.post<{ accessToken: string }>(
        API_ENDPOINTS.AUTH.REFRESH,
        { refreshToken },
        false,
      );

      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.accessToken);
      return response.accessToken;
    }catch {
      await this.clearStorage();
      return null;
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await apiService.post(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, {
      currentPassword,
      newPassword,
    });
  }

  async forgotPassword(email: string): Promise<void> {
    await apiService.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email }, false);
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await apiService.post(
      API_ENDPOINTS.AUTH.RESET_PASSWORD,
      { token, newPassword },
      false,
    );
  }

  async verifyOTP(email: string, otp: string): Promise<void> {
    await apiService.post(API_ENDPOINTS.AUTH.VERIFY_OTP, { email, otp }, false);
  }

  async resetPasswordWithOTP(email: string, newPassword: string): Promise<void> {
    await apiService.post(
      API_ENDPOINTS.AUTH.RESET_PASSWORD,
      { email, newPassword },
      false,
    );
  }
}

export const authService = new AuthService();
